import React, { useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Tooltip as TailwindTooltip } from '@material-tailwind/react';
import { ChartData, ChartOptions } from 'chart.js';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import Image from 'next/image';
import { useTranslate } from '@tolgee/react';
import { formatUnits } from 'viem';
import { useAccount } from 'wagmi';
import { Typography } from '@/components/Atoms';
import { Copy } from '@/components/Svg';
import WalletSvg from '@/components/Svg/WalletSvg';
import { IUserDataCommandCenterProps } from '@/types/Achievements';
import { FormatAddresses } from '@/utils/FormatAddresses';
import { BalanceLRT } from '@/utils/marketplace';
import { formatNumberDecimals, numberSeparator } from '@/utils/numberSeparator';
import { useStore } from '@/zustand/store/store';
import cn from 'classnames';
import style from '../../CommandCenter.module.scss';
import { GameUserVesting } from '@/utils/marketplace/GameUserVesting';
import { VestedAmountUser } from '@/utils/marketplace/VestedAmountUser';
import useConnectWallet from '@/hooks/common/useConnectWallet';
import CopyToClipboard from '@/utils/CopyToClipboard';

function UserWalletDataSection(props: { userData: IUserDataCommandCenterProps }) {
  const { t } = useTranslate('commandCenter');
  ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin);
  const { userData } = props;
  const { address, isConnected } = useAccount();
  const [userLrtAmount, setUserLRTAmount] = useState<any>();
  const [userVestedAmount, setUserVestedAmount] = useState('');
  const { handleClickWallet } = useConnectWallet();
  const {
    commandSocketData: { stateChanged },
  } = useStore();
  const [VestedAmountData, setVestedAmount] = useState({
    earnedVested: 0,
    purchasedVested: 0,
  });

  const resultEarnedAmount = VestedAmountUser({ address });
  const resultGameVesting = GameUserVesting({ address });

  useEffect(() => {
    if (address !== undefined && resultEarnedAmount?.data?.userVestingStats) {
      const totalVestedAmount = resultEarnedAmount?.data?.userVestingStats?.reduce((acc, item) => {
        return Number(acc) + Number(formatUnits(item.vestedAmount, 18));
      }, 0);

      setVestedAmount({ earnedVested: VestedAmountData.earnedVested, purchasedVested: totalVestedAmount });
    } else {
      setVestedAmount({ earnedVested: VestedAmountData.earnedVested, purchasedVested: 0 });
    }
  }, [resultEarnedAmount, address]);

  useEffect(() => {
    if (resultGameVesting?.data?.gameUserVestingStats) {
      const totalVestedAmount = resultGameVesting?.data?.gameUserVestingStats?.reduce((acc, item) => {
        return Number(acc) + Number(formatUnits(item.vestedAmount, 18));
      }, 0);

      setVestedAmount({ earnedVested: totalVestedAmount, purchasedVested: VestedAmountData.purchasedVested });
    } else {
      setVestedAmount({ earnedVested: 0, purchasedVested: VestedAmountData.purchasedVested });
    }
  }, [resultGameVesting, address]);

  const total = +userVestedAmount + userData?.uer_claimed + +userLrtAmount;

  const percentages = [(userLrtAmount / total) * 100, (+userVestedAmount / total) * 100, (userData?.uer_claimed / total) * 100];

  // useEffect(() => {
  //   if (address !== undefined && result.data !== undefined && result.data?.users.length !== 0) {
  //     setUserVestedAmount(formatUnits(result.data?.users[0]?.vestedAmount, 18));
  //   } else {
  //     setUserVestedAmount('0');
  //   }
  // }, [result, address]);
  const handleBalanceLRT = async () => {
    let lrtAmount = await BalanceLRT({ address });
    setUserLRTAmount(lrtAmount);
  };
  useEffect(() => {
    if (address !== undefined) {
      handleBalanceLRT();
    }
  }, [address]);

  useEffect(() => {
    if (address !== undefined && stateChanged) {
      handleBalanceLRT();
    }
  }, [stateChanged]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      annotation: {
        annotations: {
          label1: {
            type: 'label',
            xValue: 0,
            color: 'white',
            content: ['Balance', numberSeparator(formatNumberDecimals(+userLrtAmount)).toString()],
            font: [
              {
                size: 12,
              },
              {
                size: 12,
                weight: 'bold',
              },
            ],
          },
          point1: {
            type: 'point',
            xValue: 1,
            yValue: 60,
            backgroundColor: 'transparent',
            borderColor: '#f5f5f51c',
            radius: 140,
          },
        },
      },
      legend: {
        position: 'center',
      },
    },
    //@ts-ignore
    cutout: '80%',
  };

  const data: ChartData<'pie'> = {
    labels: [],
    datasets: [
      {
        data: percentages,
        backgroundColor: ['#303030', '#656565', '#979797'],
        borderWidth: 0,
        spacing: 2,
        borderRadius: 0,
      },
    ],
  };

  return (
    <>
      {isConnected ? (
        <>
          <div>
            <div className='w-full h-[75px] flex justify-center'>
              <div className='w-[75px] '>
                {/* @ts-ignore */}
                <Doughnut data={data} options={options} />
              </div>
            </div>
            <div className='w-full flex justify-center gap-[10px] mt-1'>
              <div className='flex items-center'>
                <Typography className='text-[#888EA8] text-[11px]'>{t('LEFT_SECTION.walletSection.walletAvailable.balance')}</Typography>
                <div className='w-[4px] h-[4px] rounded-full bg-[#303030 ] ml-1'></div>
              </div>
              <div className='flex items-center'>
                <Typography className='text-[#888EA8] text-[11px]'>{t('LEFT_SECTION.walletSection.walletAvailable.staked')}</Typography>
                <div className='w-[4px] h-[4px] rounded-full bg-[#979797] ml-1'></div>
              </div>
              <div className='flex items-center'>
                <Typography className='text-[#888EA8] text-[11px]'>{t('LEFT_SECTION.walletSection.walletAvailable.claimed')}</Typography>
                <div className='w-[4px] h-[4px] rounded-full bg-[#656565] ml-1'></div>
              </div>
            </div>
            <div className='w-full flex flex-col items-start justify-start '>
              <div className='w-full flex items-center gap-1'>
                <Typography size='s16ToS14'>{t('LEFT_SECTION.walletSection.walletAvailable.title')}</Typography>
                {isConnected ? (
                  <Typography size='s12ToS12' className='text-[#05E900]'>
                    connected
                  </Typography>
                ) : (
                  <Typography size='s12ToS12' className='text-[#e90000]'>
                    Not Connected
                  </Typography>
                )}
              </div>
              <div className='flex items-center gap-1'>
                <Typography size='s12ToS12'>{t('LEFT_SECTION.walletSection.walletAvailable.address')}:</Typography>
                <Typography size='s12ToS12' className='text-[#0BE9F8]'>
                  {FormatAddresses(address)}
                </Typography>
                <TailwindTooltip content='Copy Wallet Address' placeholder='' onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <div className='cursor-pointer' onClick={() => CopyToClipboard(address, { successMessageKey: 'Wallet Address Copied!' })}>
                    <Copy style={{ width: 12, height: 12 }} />
                  </div>
                </TailwindTooltip>
              </div>
              <div className='flex items-center gap-1'>
                <Typography size='s12ToS12'>{t('LEFT_SECTION.walletSection.walletAvailable.total')}:</Typography>
                <Typography size='s12ToS12' className='text-[#FF7F09]'>
                  {numberSeparator(formatNumberDecimals(+userLrtAmount))}
                </Typography>
              </div>
              <div className='flex items-center gap-1'>
                <Typography size='s12ToS12'>Earned Vested:</Typography>
                <Typography size='s12ToS12' className='text-[#FF7F09]'>
                  {numberSeparator(formatNumberDecimals(+VestedAmountData.earnedVested))}
                </Typography>
              </div>
              <div className='flex items-center gap-1'>
                <Typography size='s12ToS12'>Purchased Vested:</Typography>
                <Typography size='s12ToS12' className='text-[#FF7F09]'>
                  {numberSeparator(formatNumberDecimals(+VestedAmountData.purchasedVested))}
                </Typography>
              </div>
              <div className='flex items-center gap-1'>
                <Typography size='s12ToS12'>{t('LEFT_SECTION.walletSection.walletAvailable.lrtReward')}:</Typography>
                <Typography size='s12ToS12' className='text-[#FF7F09]'>
                  {numberSeparator(formatNumberDecimals(userData ? +userData?.uer_token_earned_in_command_center : 0))}
                </Typography>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className='text-center flex flex-col items-center justify-center'>
          <WalletSvg />
          <Typography className='px-8 mt-1' size='s12ToS12'>
            {t('LEFT_SECTION.walletSection.walletUnAvailable.descFirst')}{' '}
            <Typography
              className={cn(
                style.hoverAnimation,
                'text-[#06BFB4] cursor-pointer gtm-command_center_wallet-connect', // Conditionally add another class if needed
              )}
              isBold
              onClick={handleClickWallet}
            >
              {t('LEFT_SECTION.walletSection.walletUnAvailable.highlightDesc')}
            </Typography>{' '}
            {t('LEFT_SECTION.walletSection.walletUnAvailable.lastDesc')}
          </Typography>
        </div>
      )}
    </>
  );
}

export default UserWalletDataSection;
